import { readMe, customEndpoint, createUser, createItem, readItems } from '@directus/sdk'
import directus, { storage } from '@/lib/directus'
import { User } from '@/types'
import axios from 'axios'

interface AuthResponse {
  success: boolean
  error?: Error
  user?: User
  token?: string
  redirectTo?: string
}

interface LoginCredentials {
  email: string
  password: string
}

interface RegisterData {
  email: string
  password: string
  firstName: string
  lastName: string
  phoneNumber?: string
}

export const authService = {
  /**
   * Login with email and password using Directus SDK
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const result = await directus.login(credentials.email, credentials.password)

      const me = await directus.request(readMe())

      console.log('📤 User data from Directus:', me)

      // Récupérer les données commerciales depuis commercial_relations
      let commercialPhone: string | undefined
      let commercialBalance = 0
      let commercialId: string | undefined
      let referralCode: string | undefined
      let totalEarnings = 0
      let referralCount = 0

      try {
        const commercialRelations = await directus.request(readItems('commercial_relations', {
          filter: {
            user_id: {
              id: {
                _eq: me.id
              }
            }
          }
        }))

        if (commercialRelations && commercialRelations.length > 0) {
          const relation = commercialRelations[0]
          commercialPhone = relation.commercial_phoneNumber
          commercialBalance = relation.balance || 0
          commercialId = relation.commercialId
          referralCode = relation.referralCode
          totalEarnings = relation.totalEarnings || 0
          referralCount = relation.referralCount || 0
          console.log('📦 Commercial relations data:', relation)
        }
      } catch (commercialError) {
        console.warn('⚠️ Erreur lors de la récupération des relations commerciales:', commercialError)
      }

      // Store user data
      const userData: User = {
        id: me.id,
        email: me.email,
        displayName: `${me.first_name || ''} ${me.last_name || ''}`.trim() || me.email,
        phone_number: commercialPhone || (me.commercialId as string | undefined) || undefined,
        commercialId: commercialId || (me.commercialId as string | undefined) || undefined,
        referralCode: referralCode || (me.referralCode as string | undefined) || undefined,
        totalEarnings: totalEarnings || me.totalEarnings || 0,
        referralCount: referralCount || me.referralCount || 0,
        balance: commercialBalance || me.balance || 0,
        accountType: me.accountType || 'MOBCASH',
        createdAt: me.date_created ? new Date(me.date_created) : new Date(),
        lastActivity: me.date_updated ? new Date(me.date_updated) : new Date(),
        photoURL: ((me.avatar as string | null) || null) || undefined
      }

      console.log('✅ Final user data:', userData)

      localStorage.setItem('userData', JSON.stringify(userData))

      return {
        success: true,
        user: userData,
        token: result.access_token || undefined,
        redirectTo: '/affiliate'
      }
    } catch (error: any) {
      console.error('Login error:', error)
      return {
        success: false,
        error: new Error(error.message || 'Email ou mot de passe incorrect')
      }
    }
  },

  /**
   * Register new user using Directus
   */
  async register(data: RegisterData): Promise<AuthResponse> {
    try {
      const commercialId = this.generateCommercialId()
      const referralCode = this.generateReferralCode()

      console.log('📝 Tentative d\'inscription pour:', data.email)

      const API_URL = 'https://ftx-pay.futurix.xyz'

      try {
        // 1. Créer l'utilisateur via l'endpoint /users/register
        const registerResponse = await axios.post(
          `${API_URL}/users/register`,
          {
            email: data.email,
            password: data.password
          },
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        )

        console.log('✅ Utilisateur créé, statut:', registerResponse.status)

        // 2. Connecter l'utilisateur
        const result = await directus.login(data.email, data.password)

        // 3. Récupérer les infos de l'utilisateur connecté
        const me = await directus.request(readMe())
        console.log('📤 User data after register:', me)

        // 4. Créer la relation commercial
        try {
          await directus.request(createItem('commercial_relations', {
            user_id: me.id,
            commercial_phoneNumber: data.phoneNumber,
            nom: data.lastName,
            prenom: data.firstName,
          }))
          console.log('✅ Relation commerciale créée')
        } catch (commercialError: any) {
          console.warn('⚠️ Erreur lors de la création de la relation commerciale:', commercialError)
        }

        // 5. Retourner les données utilisateur complètes
        const userData: User = {
          id: me.id,
          email: me.email,
          displayName: `${me.first_name || ''} ${me.last_name || ''}`.trim() || me.email,
          phone_number: data.phoneNumber,
          commercialId: commercialId,
          referralCode: referralCode,
          totalEarnings: 0,
          referralCount: 0,
          balance: 0,
          accountType: 'MOBCASH',
          createdAt: me.date_created ? new Date(me.date_created) : new Date(),
          lastActivity: me.date_updated ? new Date(me.date_updated) : new Date(),
          photoURL: ((me.avatar as string | null) || null) || undefined
        }

        localStorage.setItem('userData', JSON.stringify(userData))

        return {
          success: true,
          user: userData,
          token: result.access_token || undefined,
          redirectTo: '/affiliate'
        }

      } catch (apiError: any) {
        console.error('❌ Erreur API lors de l\'inscription:', apiError.response?.data || apiError.message)

        const errorMessage = apiError.response?.data?.errors?.[0]?.message || apiError.response?.data?.error?.message || apiError.message

        // Messages d'erreur spécifiques
        if (apiError.response?.status === 409 || errorMessage.includes('already exists') || errorMessage.includes('existe déjà')) {
          return {
            success: false,
            error: new Error('Un compte avec cet email existe déjà')
          }
        }

        return {
          success: false,
          error: new Error(errorMessage || 'Erreur lors de la création du compte')
        }
      }

    } catch (error: any) {
      console.error('Register error:', error)
      return {
        success: false,
        error: new Error(error.message || 'Erreur lors de l\'inscription')
      }
    }
  },

  /**
   * Logout user using Directus SDK
   * Nettoie toutes les données d'authentification (userData + token Directus)
   * pour éviter qu'une page se comporte comme si l'utilisateur était encore connecté.
   */
  async logout(): Promise<void> {
    try {
      await directus.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      // Toujours nettoyer le localStorage, même si directus.logout() échoue
      localStorage.removeItem('userData')
      localStorage.removeItem('directus-data')
    }
  },

  isAuthenticated(): boolean {
    try {
      const data = storage.get()
      return !!data?.access_token
    } catch {
      return false
    }
  },

  /**
   * Get current authenticated user
   */
  getCurrentUser(): User | null {
    try {
      const userData = localStorage.getItem('userData')
      return userData ? JSON.parse(userData) : null
    } catch (error) {
      console.error('Error parsing user data:', error)
      return null
    }
  },

  generateCommercialId(): string {
    const timestamp = Date.now().toString(36)
    const random = Math.random().toString(36).substring(2, 8)
    return `MB${timestamp.toUpperCase()}${random.toUpperCase()}`
  },

  /**
   * Generate unique referral code
   */
  generateReferralCode(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
    let result = ''
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    return result
  },

  /**
   * Update current user data
   */
  updateCurrentUser(userData: Partial<User>): void {
    const currentUser = this.getCurrentUser()
    if (currentUser) {
      const updatedUser = { ...currentUser, ...userData }
      localStorage.setItem('userData', JSON.stringify(updatedUser))
    }
  },

  /**
   * Refresh user data from Directus API
   */
  async refreshUserData(): Promise<AuthResponse> {
    try {
      const me = await directus.request(readMe())

      // Récupérer les données commerciales depuis commercial_relations
      let commercialPhone: string | undefined
      let commercialBalance = 0
      let commercialId: string | undefined
      let referralCode: string | undefined
      let totalEarnings = 0
      let referralCount = 0

      try {
        const commercialRelations = await directus.request(readItems('commercial_relations', {
          filter: {
            user_id: {
              id: {
                _eq: me.id
              }
            }
          }
        }))

        if (commercialRelations && commercialRelations.length > 0) {
          const relation = commercialRelations[0]
          commercialPhone = relation.commercial_phoneNumber
          commercialBalance = relation.balance || 0
          commercialId = relation.commercialId
          referralCode = relation.referralCode
          totalEarnings = relation.totalEarnings || 0
          referralCount = relation.referralCount || 0
        }
      } catch (commercialError) {
        console.warn('Erreur lors de la récupération des relations commerciales:', commercialError)
      }

      const userData: User = {
        id: me.id,
        email: me.email,
        displayName: `${me.first_name || ''} ${me.last_name || ''}`.trim() || me.email,
        phone_number: commercialPhone || (me.commercialId as string | undefined) || undefined,
        commercialId: commercialId || (me.commercialId as string | undefined) || undefined,
        referralCode: referralCode || (me.referralCode as string | undefined) || undefined,
        totalEarnings: totalEarnings || me.totalEarnings || 0,
        referralCount: referralCount || me.referralCount || 0,
        balance: commercialBalance || me.balance || 0,
        accountType: me.accountType || 'MOBCASH',
        createdAt: me.date_created ? new Date(me.date_created) : new Date(),
        lastActivity: me.date_updated ? new Date(me.date_updated) : new Date(),
        photoURL: ((me.avatar as string | null) || null) || undefined
      }

      this.updateCurrentUser(userData)

      return {
        success: true,
        user: userData
      }
    } catch (error: any) {
      return {
        success: false,
        error: new Error(error.message || 'Erreur lors du rafraîchissement')
      }
    }
  }
}

export default authService